import { getDisplayStatus } from '../Field/useIsFieldHidden';
import { useProductOptions } from '../FormFields/ClientConfiguredFields/ProductOptions/ProductOptionsProvider';
import {
    FieldFragment,
    Field_WorkEntityField_Fragment,
    StructuredBriefFragment,
} from '../brief.generated';
import { FieldGroupData, FieldGroupVariant, fieldGroups } from './FieldGroup';
import type { DisplayStatus } from '@99designs/graph-utils/types';

export type WorkEntityDisplay = {
    title: string;
    description: string | null;
    field: Field_WorkEntityField_Fragment;
};

interface FormGroupData {
    intro: FieldGroupData;
    groups: FieldGroupData[];
    hiddenFields: FieldFragment[];
    workEntityField: WorkEntityDisplay | undefined;
}

export function useWorkEntityField(brief: StructuredBriefFragment) {
    return fieldGroups(brief)
        .flatMap((g) => {
            const workEntity = formatWorkEntity(g);
            return workEntity ? [workEntity] : [];
        })
        .pop();
}

const HIDDEN_DISPLAY_STATUSES: DisplayStatus[] = [
    'DISPLAY_STATUS_HIDDEN',
    'DISPLAY_STATUS_HIDDEN_GROUP',
];

// Should the back end not be returning the data in the correct format for the front end?
// Does this not invalidate the backend driven UI pattern of the form?
// Yes to both questions. The backend driven UI pattern was more useful on the 99d site when there were multiple consumers and many more briefs.
// Here we have made the conscious decision to break that pattern in order to allow greater customisation and control over the form.
// This is not ideal and will be refactored in the future, and we will decide to go either in the direction of fully custom forms on the front end
// or a form builder driven primarily by the back end with limited UI customisation.

export function useFormGroups(
    brief: StructuredBriefFragment,
    variant: FieldGroupVariant = FieldGroupVariant.EDITABLE,
    isReview = false,
    isEditMode = false
): FormGroupData {
    const { availableOptions, selectedOptions } = useProductOptions();
    const hasDesignProductQuestion = brief.dynamicFields.some(
        (f) => f.id === 'physicalProduct' && f.groupId === 'productConfiguration'
    );

    const hiddenStepFields: FieldFragment[] = [];
    let workEntityField: WorkEntityDisplay | undefined;

    const [intro, ...groups] = fieldGroups(brief, variant)
        .filter((g) => {
            const workEntity = formatWorkEntity(g);
            if (workEntity) {
                workEntityField = workEntity;

                return false;
            }

            const isGroupHiddenByRenderCriteria = g.fields.length === 0;
            const isGroupHiddenByDisplay = g.fields.every((f) => {
                if (!f.display) {
                    return false;
                }

                const relevantDisplay = getDisplayStatus(f.display, isReview, isEditMode);
                const isHidden = HIDDEN_DISPLAY_STATUSES.includes(relevantDisplay);

                if (!isReview && isHidden) {
                    hiddenStepFields.push(f);
                }

                return isHidden;
            });
            const isGroupOptionsAndNoOptions =
                availableOptions.length === 0 && g.id === 'productConfiguration';
            const isGroupReadonlyAndNoSelectedOptions =
                g.id === 'productConfiguration' &&
                variant === FieldGroupVariant.READONLY &&
                Object.keys(selectedOptions).length === 0;

            return !(
                isGroupHiddenByRenderCriteria ||
                isGroupHiddenByDisplay ||
                (isGroupOptionsAndNoOptions && !hasDesignProductQuestion) ||
                isGroupReadonlyAndNoSelectedOptions
            );
        })
        .map((g, i) => new FieldGroupData(g.id, g.title, g.description, g.fields, i === 0));
    return { intro, groups, hiddenFields: hiddenStepFields, workEntityField };
}

function formatWorkEntity(fieldGroupData: FieldGroupData) {
    const field = fieldGroupData.fields
        .flatMap((f) => (f.__typename === 'WorkEntityField' ? [f] : []))
        .pop();
    return field
        ? {
              title: fieldGroupData.title,
              description: fieldGroupData.description,
              field,
          }
        : undefined;
}
